import type { ProceduralDef } from './types';
import {
  playerShipBase,
  playerShipPods,
  playerShadow,
  playerThrust,
} from './procedural/player-ship';
import { bulletPlayerBasic,
  bulletPlayerMissile, bulletEnemyBall, bulletEnemyRing , bulletEnemyFire } from './procedural/bullets';
import { bulletEnemyVoid, bulletEnemyRock, bulletEnemyWind,
  bulletEnemyBolt, bulletEnemyShard, bulletEnemySpore, bulletEnemyStar,
  bulletEnemyLensBolt } from './procedural/bullets-boss';
import { bulletEnemyBomb, bulletEnemyEgg } from './procedural/bullets-fused';
import {
  bulletPlayerPlasma,
  bulletPlayerLaser,
  bulletPlayerBomb,
  mountBullet,
  mountPlasma,
  mountLaser,
  mountBomb,
} from './procedural/weapon-assets';
import { weaponGlyphDefs } from './procedural/weapon-glyphs';
import { envPit, envLavaPool, envPortalGate } from './procedural/env';
import {
  envGroundScrap, envWallMassScrap, envWallScrap, envRockScrap, envLandmarkScrap,
  envGroundCore, envWallMassCore, envWallCore, envRockCore, envLandmarkCore,
} from './procedural/env-scrap-core';
import {
  envGroundBrood, envWallMassBrood, envWallBrood, envRockBrood, envLandmarkBrood,
  envGroundRim, envWallMassRim, envWallRim, envRockRim, envLandmarkRim,
} from './procedural/env-brood-rim';
import { envDecalScrap, envDecalCore, envDecalBrood, envDecalRim } from './procedural/env-decals';
import { envPropScrap, envPropCore, envPropBrood, envPropRim } from './procedural/env-props';
import {
  envGateField, envGatePylon, envMote, envAcidPool, envRiftPool, uiObjectiveArrow,
} from './procedural/env-route';
import { gemYellow, gemRed, pickupShield, pickupRepair, pickupTrail } from './procedural/pickups';
import {
  gemYellowCluster, gemYellowHoard, gemRedCluster, gemRedHoard, pickupShieldX2, pickupRepairX2,
} from './procedural/pickups-big';
import {
  bossSentinelBody,
  bossSentinelRing,
  bossSentinelDrone,
} from './procedural/boss-sentinel';
import { bossCarrierBody, bossCarrierCrystal, bossCarrierTurret } from './procedural/boss-carrier';
import {
  bossHiveBody,
  bossHiveShield,
  bossHiveFly,
  bossHiveElite,
} from './procedural/boss-hive';
import { bossSerpentHead, bossSerpentSegment } from './procedural/boss-serpent';
import { bossKrakenBody, bossKrakenSegment, bossKrakenTip } from './procedural/boss-kraken';
import { bossCentipedeHead, bossCentipedeSegment } from './procedural/boss-centipede';
import { bossQuartetClone } from './procedural/boss-quartet';
import { bossPyreBody } from './procedural/boss-pyre';
import { bossTideBody } from './procedural/boss-tide';
import { bossPrismBody } from './procedural/boss-prism';
import { bossNullBody } from './procedural/boss-null';
import { bossTerraBody } from './procedural/boss-terra';
import { bossGaleBody } from './procedural/boss-gale';
import { bossAvatarBody } from './procedural/boss-avatar';
import { bossMirrorBody } from './procedural/boss-mirror';
import { bossSplitterBody } from './procedural/boss-splitter';
import { bossAdmiralBody } from './procedural/boss-admiral';
import { bossChronosBody } from './procedural/boss-chronos';
import { bossMagnetBody } from './procedural/boss-magnet';
import { bossBastionBody } from './procedural/boss-bastion';
import { bossStalkerBody } from './procedural/boss-stalker';
import { bossSingularityBody } from './procedural/boss-singularity';
import { bossBroodwormHead, bossBroodwormSegment } from './procedural/boss-broodworm';
import { bossWebspinnerBody } from './procedural/boss-webspinner';
import { minionBunker } from './procedural/minions';
import {
  minionTurret, minionDome, minionDasher, minionWeaver,
  minionSplitter, minionMite, minionSniper,
} from './procedural/minions-light';
import { bossWardenBody, bossWardenTurret } from './procedural/boss-warden';
import { fxBossCracks } from './procedural/ui-fx';
import { fxExplosionM, fxExplosionPlayer, fxHurtRing } from './procedural/fx-blasts';
import { crateDefs } from './procedural/crates';

/**
 * The single registry of ALL asset keys.
 * New asset = generator file + one line here.
 * When moving to spritesheets the resolver looks these keys up in the atlas (docs/04-assets.md).
 */
const defs: ProceduralDef[] = [
  // Player
  playerShipBase,
  playerShipPods,
  playerShadow,
  playerThrust,
  // Weapon mounts
  mountBullet,
  mountPlasma,
  mountLaser,
  mountBomb,
  // Projectiles
  bulletPlayerBasic,
  bulletPlayerPlasma,
  bulletPlayerLaser,
  bulletPlayerBomb,
  bulletPlayerMissile,
  bulletEnemyBall,
  bulletEnemyRing,
  bulletEnemyFire,
  bulletEnemyVoid,
  bulletEnemyRock,
  bulletEnemyWind,
  bulletEnemyBolt,
  bulletEnemyShard,
  bulletEnemySpore,
  bulletEnemyStar,
  bulletEnemyBomb,
  bulletEnemyEgg,
  bulletEnemyLensBolt,
  // Environment — shared
  envPit,
  envLavaPool,
  envAcidPool,
  envRiftPool,
  envPortalGate,
  envGateField,
  envGatePylon,
  envMote,
  // Environment — sector theme kits (config/themes.ts)
  envGroundScrap, envWallMassScrap, envWallScrap, envRockScrap, envLandmarkScrap,
  envDecalScrap, envPropScrap,
  envGroundCore, envWallMassCore, envWallCore, envRockCore, envLandmarkCore,
  envDecalCore, envPropCore,
  envGroundBrood, envWallMassBrood, envWallBrood, envRockBrood, envLandmarkBrood,
  envDecalBrood, envPropBrood,
  envGroundRim, envWallMassRim, envWallRim, envRockRim, envLandmarkRim,
  envDecalRim, envPropRim,
  // Boss: Sentinel
  bossSentinelBody,
  bossSentinelRing,
  bossSentinelDrone,
  // Boss: Crystal Carrier
  bossCarrierBody,
  bossCarrierTurret,
  bossCarrierCrystal,
  // Boss: Hive Mother
  bossHiveBody,
  bossHiveShield,
  bossHiveFly,
  bossHiveElite,
  // Boss: Twin Serpents
  bossSerpentHead,
  bossSerpentSegment,
  // Boss: Kraken
  bossKrakenBody,
  bossKrakenSegment,
  bossKrakenTip,
  // Boss: Centipede
  bossCentipedeHead,
  bossCentipedeSegment,
  // Boss: Quartet
  bossQuartetClone,
  // Boss: Elementals
  bossPyreBody,
  bossTideBody,
  bossPrismBody,
  bossNullBody,
  bossTerraBody,
  bossGaleBody,
  bossAvatarBody,
  bossMirrorBody,
  bossSplitterBody,
  bossAdmiralBody,
  bossChronosBody,
  bossMagnetBody,
  bossBastionBody,
  bossStalkerBody,
  bossSingularityBody,
  bossBroodwormHead,
  bossBroodwormSegment,
  bossWebspinnerBody,
  // Wave minions
  minionBunker,
  minionTurret,
  minionDome,
  minionDasher,
  minionWeaver,
  minionSplitter,
  minionMite,
  minionSniper,
  // Boss: Warden
  bossWardenBody,
  bossWardenTurret,
  // UI / FX
  fxExplosionM,
  fxExplosionPlayer,
  fxHurtRing,
  fxBossCracks,
  uiObjectiveArrow,
  // Weapon glyph icons — one per weapon id (HUD strip, shop bays)
  ...weaponGlyphDefs,
  // Pickups
  gemYellow,
  gemRed,
  pickupShield,
  pickupRepair,
  pickupTrail,
  // Big loot — own silhouettes, not scaled-up sprites (polish 1)
  gemYellowCluster,
  gemYellowHoard,
  gemRedCluster,
  gemRedHoard,
  pickupShieldX2,
  pickupRepairX2,
  // Weapon crates — one per weapon id (glyph lid + rarity rim)
  ...crateDefs,
];

export const MANIFEST: ReadonlyMap<string, ProceduralDef> = new Map(
  defs.map((d) => [d.key, d]),
);
